import React, { useState, useEffect, useMemo } from 'react';
import Decimal from 'decimal.js';
import config from '../../src/config/config';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogActions,
  Typography, Grid, TextField, Button, Box, Divider,
  Paper, IconButton, Tooltip, Stack, Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import SimpleSuccessSpinner from '../components/LoadingSpinner';
import handleOpenSweetAlert from '../components/handleOpenSweetAlert';
import DoubleConfirmModal from '../components/DoubleConfirmModal';

const ModalConfirmarPrecios = ({ open, handleClose, data, handleDataUpdate }) => {
  // 🧠 Estados
  const [updatedMontoSolicitado, setUpdatedMontoSolicitado] = useState('');
  const [nuevaFechaVencimiento, setNuevaFechaVencimiento] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [errors, setErrors] = useState({ monto: '', fecha: '' });

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const apiUrl = config.apiBaseUrl;

  // 📌 Datos base
  const montoSolicitado = parseFloat(data?.MONTO_SOLICITADO || 0);
  const totalOrden = parseFloat(data?.TOTAL_OC || 0);
  const soloActualizarFecha = montoSolicitado === totalOrden;

  // —— Estilos de inputs (uniformes, compactos)
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.25,
      backgroundColor: '#fbfbfd',
      transition: 'box-shadow .15s, border-color .15s',
      '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
      '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
      '&.Mui-focused fieldset': { borderColor: '#0655A5' },
      '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(6,85,165,0.10)' },
    },
    '& .MuiInputLabel-root': { fontWeight: 600 },
    '& .MuiFormHelperText-root': { ml: 0 },
  };

  // 🔧 Utilidades
  const formatCurrency = (amount) => {
    if (amount === '' || isNaN(amount)) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };
  const parseNumber = (value) => {
    if (typeof value !== 'string') return '';
    const number = parseFloat(value.replace(/[^0-9.]/g, ''));
    return isNaN(number) ? '' : number;
  };
  const validateInput = (value) => /^(?=.*\d)[0-9.,]+$/.test((value || '').replace(/[^0-9.,]/g, ''));

  // 🖊 Eventos
  const handleMontoChange = (e) => {
    const value = e.target.value.replace(/[^0-9.,]/g, '');
    setUpdatedMontoSolicitado(value);
    if (errors.monto) setErrors((prev) => ({ ...prev, monto: '' }));
  };
  const handleMontoBlur = () => {
    const raw = (updatedMontoSolicitado || '').replace(/[^0-9.,]/g, '');
    if (!raw || !validateInput(updatedMontoSolicitado)) {
      setErrors((prev) => ({ ...prev, monto: 'Ingrese un monto válido.' }));
      setUpdatedMontoSolicitado('');
      return;
    }
    setUpdatedMontoSolicitado(formatCurrency(parseNumber(updatedMontoSolicitado)));
  };
  const handleFechaChange = (e) => {
    const selected = e.target.value;
    if (selected < today) {
      setErrors((prev) => ({ ...prev, fecha: 'La fecha no puede ser anterior a hoy.' }));
      setNuevaFechaVencimiento('');
    } else {
      setNuevaFechaVencimiento(selected);
      setErrors((prev) => ({ ...prev, fecha: '' }));
    }
  };

  const resetAndClose = () => {
    setUpdatedMontoSolicitado('');
    setNuevaFechaVencimiento('');
    setErrors({ monto: '', fecha: '' });
    handleClose();
  };

  // 🔄 Cargar datos al abrir modal
  useEffect(() => {
    if (open && data?.MONTO_SOLICITADO != null) {
      setUpdatedMontoSolicitado(
        formatCurrency(parseNumber(String(data.MONTO_SOLICITADO)))
      );
      setNuevaFechaVencimiento(data.FECHA_VENCIMIENTO || '');
    }
  }, [open, data]);

  // 💾 Guardar cambios (confirmación final)
  const handleSaveClick = async () => {
    setLoading(true);
    setErrors({ monto: '', fecha: '' });

    if (!nuevaFechaVencimiento) {
      setErrors((prev) => ({ ...prev, fecha: 'Ingrese la nueva fecha.' }));
      setLoading(false);
      return;
    }
    if (!updatedMontoSolicitado) {
      setErrors((prev) => ({ ...prev, monto: 'Ingrese un monto válido.' }));
      setLoading(false);
      return;
    }

    try {
      const montoActualizado = new Decimal(updatedMontoSolicitado.replace(/[^0-9.-]+/g, ''));
      const montoTotal = new Decimal(String(data.TOTAL_OC).replace(/[^0-9.-]+/g, ''));
      const saldoRestante = new Decimal(String(data.RESTANTE).replace(/[^0-9.-]+/g, ''));
      const montoSolicitadoActual = new Decimal(String(data.MONTO_SOLICITADO).replace(/[^0-9.-]+/g, ''));

      // Solo fecha (mismo monto)
      if (montoActualizado.equals(montoSolicitadoActual)) {
        await axios.post(`${apiUrl}/confirmarPreciosactualizarMonto`, {
          idFolio: data.AUTSOLPAGOID,
          folio: data.FOLIO_OC,
          montoSolicitado: montoActualizado.toString(),
          nuevaFechaVencimiento
        });
        handleOpenSweetAlert('La fecha fue actualizada correctamente.', 'success');
        handleDataUpdate();
        handleClose();
        return;
      }

      // Si el nuevo monto es mayor
      if (montoActualizado.greaterThan(montoSolicitadoActual)) {
        const res = await axios.post(`${apiUrl}/getRestanteYSolicitado`, {
          folio: data.FOLIO_OC,
          idFolio: data.AUTSOLPAGOID,
          idEmpresa: data.EMPRESAID
        });

        const restante = new Decimal(res.data.restante);
        const solicitadoPrevio = new Decimal(res.data.montoSolicitado);
        const sumaControl = restante.plus(solicitadoPrevio);

        if (montoActualizado.greaterThan(sumaControl)) {
          setErrors((prev) => ({ ...prev, monto: 'El monto excede el restante permitido.' }));
          setLoading(false);
          return;
        }

        const nuevoRestante = sumaControl.minus(montoActualizado);
        const nuevoSolicitado = restante.minus(nuevoRestante);

        await axios.post(`${apiUrl}/confirmarPreciosActualizarVencido`, {
          idFolio: data.AUTSOLPAGOID,
          folio: data.FOLIO_OC,
          montoSolicitado: montoActualizado.toString(),
          nuevaFechaVencimiento,
          nuevoRestante: nuevoRestante.toString(),
          sumaSolicitado: Number(nuevoSolicitado.toString()),
          idEmpresa: data.EMPRESAID
        });

        handleOpenSweetAlert('Actualización realizada correctamente.', 'success');
        handleDataUpdate();
        handleClose();
        return;
      }

      // Validaciones por cantidad de pagos
      const pagos = await axios.post(`${apiUrl}/confirmarPreciosPagosRealizados`, {
        folio: data.FOLIO_OC
      });
      const count = pagos.data.count;

      if (count === 1) {
        const diferencia = montoTotal.minus(montoActualizado);
        const margen = diferencia.dividedBy(montoTotal);

        if (margen.gt(0.15)) {
          if (montoActualizado.greaterThan(saldoRestante)) {
            setErrors((prev) => ({ ...prev, monto: 'El monto no puede superar el saldo restante.' }));
            setLoading(false);
            return;
          }
          if (montoActualizado.lessThan(montoSolicitadoActual)) {
            setErrors((prev) => ({ ...prev, monto: 'Debe ser mayor o igual al monto solicitado anterior.' }));
            setLoading(false);
            return;
          }
        } else {
          if (!montoActualizado.equals(montoTotal)) {
            setErrors((prev) => ({ ...prev, monto: 'Debe ser igual al monto total de la orden.' }));
            setLoading(false);
            return;
          }
        }
      } else if (count >= 2) {
        if (montoActualizado.greaterThan(saldoRestante)) {
          setErrors((prev) => ({ ...prev, monto: 'Supera el saldo restante.' }));
          setLoading(false);
          return;
        }
        if (montoActualizado.lessThan(montoSolicitadoActual)) {
          setErrors((prev) => ({ ...prev, monto: 'Debe ser mayor o igual al monto anterior.' }));
          setLoading(false);
          return;
        }
      }

      // ✅ Actualizar monto
      await axios.post(`${apiUrl}/confirmarPreciosactualizarMonto`, {
        idFolio: data.AUTSOLPAGOID,
        folio: data.FOLIO_OC,
        montoSolicitado: montoActualizado.toString(),
        nuevaFechaVencimiento
      });

      handleOpenSweetAlert('El cambio fue realizado correctamente.', 'success');
      handleDataUpdate();
      handleClose();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al actualizar.';
      setErrors({ monto: errorMsg, fecha: '' });
      handleOpenSweetAlert(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // 🛑 Confirmación previa
  const handleConfirmGuardar = (e) => {
    e?.preventDefault?.();
    const montoValido = updatedMontoSolicitado && parseNumber(updatedMontoSolicitado) > 0;
    if (!montoValido) return setErrors((prev) => ({ ...prev, monto: 'Ingrese un monto válido.' }));
    if (!nuevaFechaVencimiento) return setErrors((prev) => ({ ...prev, fecha: 'Ingrese una fecha.' }));
    setConfirmModalOpen(true);
  };

  // —— UI
  return (
    <>
      <Dialog
        open={open}
        disableRestoreFocus
        onClose={resetAndClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,0.18)' } }}
      >
        {/* Header compacto y consistente */}
        <Box
          sx={{
            p: 1.25,
            background: 'linear-gradient(90deg, #0655A5 0%, #0B6BCB 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: '1px solid rgba(255,255,255,.15)'
          }}
        >
          <Avatar sx={{ width: 26, height: 26, bgcolor: 'white', color: '#0655A5' }}>
            <PaidRoundedIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1.1 }}>
              Actualización de precios
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.95 }}>
              {data?.FOLIO_OC ? `OC ${data.FOLIO_OC}` : ''} {data?.PROVEEDORTEMP ? `• ${data.PROVEEDORTEMP}` : ''}
            </Typography>
          </Box>
          <Tooltip title="Cerrar">
            <IconButton onClick={resetAndClose} sx={{ color: 'white' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {/* Datos OC */}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Avatar sx={{ width: 22, height: 22, bgcolor: '#0655A5' }}>
                  <DescriptionRoundedIcon fontSize="small" />
                </Avatar>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Resumen de la Orden
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2"><strong>Folio:</strong> {data?.FOLIO_OC ?? 'N/D'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2"><strong>Total OC:</strong> {formatCurrency(parseNumber(String(data?.TOTAL_OC ?? 0)))}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2"><strong>Centro de Costo:</strong> {data?.NUMERO_CENTRO_COSTOS ?? 'N/D'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2"><strong>Departamento:</strong> {data?.DEPARTAMENTO ?? 'N/D'}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2"><strong>Proveedor:</strong> {data?.PROVEEDORTEMP ?? 'N/D'}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Actualización */}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Avatar sx={{ width: 22, height: 22, bgcolor: '#0655A5' }}>
                  <CalendarMonthRoundedIcon fontSize="small" />
                </Avatar>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Actualizar monto y/o fecha
                </Typography>
              </Box>

              {soloActualizarFecha && (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'rgba(6,85,165,0.06)',
                    border: '1px solid rgba(6,85,165,0.2)'
                  }}
                >
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>
                    El monto solicitado ya coincide con el total de la OC. Solo puedes actualizar la fecha.
                  </Typography>
                </Box>
              )}

              <Grid container spacing={1.25}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    size="small"
                    label="Monto actual"
                    value={formatCurrency(parseNumber(String(data?.MONTO_SOLICITADO ?? 0)))}
                    fullWidth
                    disabled
                    sx={inputSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    size="small"
                    label="Nuevo monto"
                    value={updatedMontoSolicitado}
                    onChange={handleMontoChange}
                    onBlur={handleMontoBlur}
                    error={!!errors.monto}
                    helperText={errors.monto}
                    fullWidth
                    disabled={soloActualizarFecha}
                    sx={inputSx}
                    inputProps={{ inputMode: 'decimal' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    size="small"
                    label="Nueva fecha de vencimiento"
                    type="date"
                    value={nuevaFechaVencimiento}
                    onChange={handleFechaChange}
                    error={!!errors.fecha}
                    helperText={errors.fecha}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: today }}
                    fullWidth
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </DialogContent>

        <Divider />

        {/* Acciones pegadas (claras y limpias) */}
        <DialogActions
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            backdropFilter: 'saturate(120%) blur(6px)',
            backgroundColor: 'rgba(255,255,255,0.9)',
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 1, px: 2
          }}
        >
          <Button variant="outlined" onClick={resetAndClose} disabled={loading}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmGuardar} disabled={loading}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spinner */}
      <SimpleSuccessSpinner open={loading} handleClose={() => setLoading(false)} message="Procesando..." />

      {/* Confirmación final */}
      <DoubleConfirmModal
        open={confirmModalOpen}
        handleConfirm={() => {
          setConfirmModalOpen(false);
          handleSaveClick();
        }}
        handleCancel={() => setConfirmModalOpen(false)}
        message={
          <>
            <Typography variant="body1" gutterBottom>¿Confirmas actualizar el precio y la fecha?</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Typography fontWeight="bold">Monto anterior:</Typography>
              <Typography color="text.secondary">{formatCurrency(parseNumber(String(data?.MONTO_SOLICITADO ?? 0)))}</Typography>
              <Typography fontWeight="bold" sx={{ mx: 1 }}>→</Typography>
              <Typography fontWeight="bold">Monto nuevo:</Typography>
              <Typography color="success.main" fontWeight="bold">
                {formatCurrency(parseNumber(updatedMontoSolicitado || '0'))}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography fontWeight="bold">Nueva fecha de vencimiento:</Typography>
              <Typography>{nuevaFechaVencimiento || '—'}</Typography>
            </Box>
          </>
        }
      />
    </>
  );
};

export default ModalConfirmarPrecios;